import { frameId } from "../assets/manifest.ts";
import { playersAbove } from "../document/band.ts";
import type { MapDocument, PropInstance } from "../document/types.ts";
import { layerDepth, tieKeys } from "../editor/sort.ts";
import { ownMarksOf, standMarks, standPropDepth, type StandFrame, type StandLookup, type StandPlacement } from "./stand-marks.ts";

/**
 * The editor's side of the stand marks (ADR-132 decision 5): the lookup of an open document, laid by the same
 * `standMarks` the compile bakes into its chunks.
 */

/**
 * The Behind and In front cells of an open map, laid as the compile lays them: the props of every prop layer in the
 * Players band, each at the depth its image draws at, keyed over its whole layer as the editor keys the layer it
 * draws. A behaviour prop the server drives is left out, as the compile leaves it out of the client's chunks; one
 * driven by presence is kept.
 *
 * @param map The open map.
 * @param frameOf Looks a frame's marks and size up (`framesOf` over the context).
 * @param isPresence Whether a prop is driven by presence.
 * @param tileSize Tile size in pixels.
 * @param band The Players band, the number of ground layers.
 * @param isStill Whether a behaviour prop holds still (`holdsStill`), so its marks are laid like a plain prop's.
 * @returns The lookup, empty when no prop is painted.
 */
export function documentStandLookup(map: MapDocument, frameOf: (frame: string) => StandFrame | undefined, isPresence: (prop: PropInstance) => boolean, tileSize: number, band: number = playersAbove(map), isStill: (prop: PropInstance) => boolean = () => false): StandLookup {
  const placements: StandPlacement[] = [];
  map.layers.forEach((layer, index) => {
    if (layer.kind !== "prop" || index < band) {
      return;
    }
    const keys = tieKeys(layer.props, isPresence);
    layer.props.forEach((prop, i) => {
      if (prop.behaviour !== undefined && !isPresence(prop) && !isStill(prop)) {
        return;
      }
      const id = frameId(prop.frame.tileset, null, prop.frame.tile);
      const frame = frameOf(id);
      if (frame === undefined) {
        return;
      }
      const depth = standPropDepth({ x: prop.x, y: prop.y, flipY: prop.flipY, sortPixels: prop.sortPixels, order: keys[i] as number }, frame, tileSize, layerDepth(index, band), map.height);
      const own = ownMarksOf(prop.footprint);
      placements.push({ frame: id, x: prop.x, y: prop.y, flipX: prop.flipX, flipY: prop.flipY, sortPixels: prop.sortPixels, depth, ...(own === undefined ? {} : { own }) });
    });
  });
  return standMarks(placements, frameOf, tileSize, map.width, map.height);
}

/**
 * Whether any prop of a map holds its own Behind or In front cells (ADR-132 decision 12), so the map has marks to
 * lay even where no frame is painted.
 *
 * @param map The open map.
 * @returns True when a placed prop's own footprint holds a mark.
 */
export function mapHoldsOwnMarks(map: MapDocument): boolean {
  return map.layers.some((layer) => layer.kind === "prop" && layer.props.some((prop) => prop.footprint !== undefined && ((prop.footprint.behind?.length ?? 0) > 0 || (prop.footprint.front?.length ?? 0) > 0)));
}
